import type { EventEmitter } from '../event';
import { ensureUnit, quantity } from '../core/lang';
import type { Attributes, AnimationDefinition, AnimationEvent } from './types';
import type { Svg } from './Svg';

/**
 * The named easing curves for {@link Svg.animate}, from `easeInSine` to
 * `easeInOutBack`: each the four numbers of a cubic Bézier curve. Name one
 * as an animation's `easing`, or pass four numbers of your own, such as
 * `easing: [0.25, 0.1, 0.25, 1]`.
 *
 * @example
 * ```ts
 * import { lineChart, findAll, Svg, type ChartPlugin } from 'chartist';
 *
 * // Points drop in from the top and bounce a little at the end.
 * const dropIn: ChartPlugin = {
 *   name: 'drop-in',
 *   install(chart) {
 *     chart.on('mount', ({ scene, elementOf }) => {
 *       for (const point of findAll(scene, { role: 'point' })) {
 *         const element = elementOf(point);
 *         const { y } = point.ctx;
 *         if (element) {
 *           new Svg(element).animate({
 *             y1: { dur: 600, from: 0, to: y, easing: 'easeOutBack' },
 *             y2: { dur: 600, from: 0, to: y, easing: 'easeOutBack' }
 *           });
 *         }
 *       }
 *     });
 *   }
 * };
 *
 * lineChart('#chart', { series: [[5, 2, 4, 3]] }, { plugins: [dropIn] });
 * ```
 */
export const easings = {
  easeInSine: [0.47, 0, 0.745, 0.715],
  easeOutSine: [0.39, 0.575, 0.565, 1],
  easeInOutSine: [0.445, 0.05, 0.55, 0.95],
  easeInQuad: [0.55, 0.085, 0.68, 0.53],
  easeOutQuad: [0.25, 0.46, 0.45, 0.94],
  easeInOutQuad: [0.455, 0.03, 0.515, 0.955],
  easeInCubic: [0.55, 0.055, 0.675, 0.19],
  easeOutCubic: [0.215, 0.61, 0.355, 1],
  easeInOutCubic: [0.645, 0.045, 0.355, 1],
  easeInQuart: [0.895, 0.03, 0.685, 0.22],
  easeOutQuart: [0.165, 0.84, 0.44, 1],
  easeInOutQuart: [0.77, 0, 0.175, 1],
  easeInQuint: [0.755, 0.05, 0.855, 0.06],
  easeOutQuint: [0.23, 1, 0.32, 1],
  easeInOutQuint: [0.86, 0, 0.07, 1],
  easeInExpo: [0.95, 0.05, 0.795, 0.035],
  easeOutExpo: [0.19, 1, 0.22, 1],
  easeInOutExpo: [1, 0, 0, 1],
  easeInCirc: [0.6, 0.04, 0.98, 0.335],
  easeOutCirc: [0.075, 0.82, 0.165, 1],
  easeInOutCirc: [0.785, 0.135, 0.15, 0.86],
  easeInBack: [0.6, -0.28, 0.735, 0.045],
  easeOutBack: [0.175, 0.885, 0.32, 1.275],
  easeInOutBack: [0.68, -0.55, 0.265, 1.55]
};

/**
 * The property of an element that holds the emitter its descendants'
 * animations report to when `animate()` gets none. The compat plugin
 * sets it on a chart's `svg`. A global symbol, so separate builds of the
 * core and `chartist/compat` share it.
 */
export const ANIMATION_EMITTER = Symbol.for('chartist.animationEmitter');

/** The emitter set on the element or its closest ancestor that has one. */
export function animationEmitterOf(node: Element): EventEmitter | undefined {
  for (
    let current: Element | null = node;
    current;
    current = current.parentElement
  ) {
    const emitter = (current as { [ANIMATION_EMITTER]?: EventEmitter })[
      ANIMATION_EMITTER
    ];
    if (emitter) {
      return emitter;
    }
  }
  return undefined;
}

export function createAnimation(
  element: Svg,
  attribute: string,
  animationDefinition: AnimationDefinition,
  createGuided = false,
  eventEmitter?: EventEmitter
) {
  const { easing, ...def } = animationDefinition;
  const attributeProperties: Attributes = {};
  let animationEasing;
  let timeout;

  // Check if an easing is specified in the definition object and delete it from the object as it will not
  // be part of the animate element attributes.
  if (easing) {
    // If already an easing Bézier curve array we take it or we lookup a easing array in the Easing object
    animationEasing = Array.isArray(easing) ? easing : easings[easing];
  }

  // If numeric dur or begin was provided we assume milli seconds
  def.begin = ensureUnit(def.begin, 'ms');
  def.dur = ensureUnit(def.dur, 'ms');

  if (animationEasing) {
    def.calcMode = 'spline';
    def.keySplines = animationEasing.join(' ');
    def.keyTimes = '0;1';
  }

  // Adding "fill: freeze" if we are in guided mode and set initial attribute values
  if (createGuided) {
    def.fill = 'freeze';
    // Animated property on our element should already be set to the animation from value in guided mode
    attributeProperties[attribute] = def.from;
    element.attr(attributeProperties);

    // In guided mode we also set begin to indefinite so we can trigger the start manually and put the begin
    // which needs to be in ms aside
    timeout = quantity(def.begin || 0).value;
    def.begin = 'indefinite';
  }

  const animate = element.elem('animate', {
    attributeName: attribute,
    ...def
  });

  if (createGuided) {
    // If guided we take the value that was put aside in timeout and trigger the animation manually with a timeout
    setTimeout(() => {
      // If beginElement fails we set the animated attribute to the end position and remove the animate element
      // This happens if the SMIL ElementTimeControl interface is not supported or any other problems occurred in
      // the browser. (Currently FF 34 does not support animate elements in foreignObjects)
      try {
        // @ts-expect-error Try legacy API.
        animate._node.beginElement();
      } catch (err) {
        // Set animated attribute to current animated value
        attributeProperties[attribute] = def.to;
        element.attr(attributeProperties);
        // Remove the animate element as it's no longer required
        animate.remove();
      }
    }, timeout);
  }

  const animateNode = animate.getNode();

  if (eventEmitter) {
    animateNode.addEventListener('beginEvent', () =>
      eventEmitter.emit<AnimationEvent>('animationBegin', {
        element: element,
        animate: animateNode,
        params: animationDefinition
      })
    );
  }

  animateNode.addEventListener('endEvent', () => {
    if (eventEmitter) {
      eventEmitter.emit<AnimationEvent>('animationEnd', {
        element: element,
        animate: animateNode,
        params: animationDefinition
      });
    }

    if (createGuided) {
      // Set animated attribute to current animated value
      attributeProperties[attribute] = def.to;
      element.attr(attributeProperties);
      // Remove the animate element as it's no longer required
      animate.remove();
    }
  });
}
